import React from 'react';

import { useRef, useEffect, useMemo } from 'react';
import {
  computePosition,
  shift,
  offset,
  arrow,
  autoUpdate,
  type Placement,
  type ComputePositionConfig
} from '@floating-ui/dom';

import type { PopoverProps } from '@deck.gl/widgets/dist/lib/components/popover';

export const Popover = ({
  placement = 'right',
  offset: pixelOffset = 0,
  arrow: arrowSize = false,
  arrowColor = 'white',
  children,
  anchorPopoverRef
}: Omit<PopoverProps, 'x' | 'y'> & { anchorPopoverRef: (el: HTMLElement | null) => void; }) => {
  const anchorRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const arrowRef = useRef<HTMLDivElement>(null);
  const updaterRef = useRef<() => void>();

  useEffect(() => {
    updaterRef.current = () => {
      if (!anchorRef.current || !contentRef.current) return;

      const arrowWidth = Array.isArray(arrowSize) ? arrowSize[0] : arrowSize || 0;
      const arrowHeight = Array.isArray(arrowSize) ? arrowSize[1] : arrowSize || 0;
      const padding = pixelOffset + Math.max(arrowHeight, arrowWidth);

      const middleware: ComputePositionConfig['middleware'] = placement.includes('-')
        ? [offset(padding), shift()]
        : [offset(padding), shift()];
      if (arrowRef.current) middleware.push(arrow({element: arrowRef.current}));
      computePosition(anchorRef.current, contentRef.current, {
        placement,
        strategy: 'fixed',
        middleware
      }).then(popoverPos => {
        if (contentRef.current) {
          Object.assign(contentRef.current.style, {
            left: `${popoverPos.x}px`,
            top: `${popoverPos.y}px`
          });
        }

        const arrowData = popoverPos.middlewareData.arrow;
        if (arrowData && arrowRef.current) {
          const arrowStyle = createArrow(arrowWidth, arrowHeight, arrowColor, popoverPos.placement);
          arrowStyle.transform = `translate(${arrowData.x || 0}px, ${arrowData.y || 0}px)`;
          Object.assign(arrowRef.current.style, arrowStyle);
        }
      });
    };
  }, [
    arrowSize,
    pixelOffset,
    placement,
    arrowColor
  ]);
  

  useMemo(() => {
    updaterRef.current?.();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [placement, arrowSize, pixelOffset]);

  useEffect(() => {
    const anchor = anchorRef.current;
    const content = contentRef.current;
    if (!anchor || !content) {
      return undefined;
    }
    content.style.visibility = 'visible';
    const cleanup = autoUpdate(anchor, content, () => updaterRef.current?.());
    return () => {
      cleanup();
    };
  }, []);

  return (
    <div
      style={{position: 'absolute', transform: `translate(-50%, calc(-100% - 50px + ${pixelOffset}px))` }}
      ref={(el) => {
        // @ts-ignore
        anchorRef.current = el;
        anchorPopoverRef(el);
      }}
    >
      <div
        className="deck-widget deck-widget-popover"
        ref={contentRef}
      >
        <>
          {Boolean(arrowSize) && (
            <div
              className="deck-widget-popover-arrow"
              style={{position: 'absolute'}}
              ref={arrowRef}
            />
          )}
          {children}
        </>
      </div>
    </div>
  );
};

function createArrow(
  width: number,
  height: number,
  color: string,
  placement: Placement
): React.CSSProperties {
  const result: React.CSSProperties = {
    width: 0,
    height: 0,
    top: '',
    bottom: '',
    left: '',
    right: ''
  };
  if (placement.startsWith('bottom')) {
    result.borderLeft = `${width / 2}px solid transparent`;
    result.borderRight = `${width / 2}px solid transparent`;
    result.borderBottom = `${height}px solid ${color}`;
    result.borderTop = '';
    result.top = `${-height}px`;
  } else if (placement.startsWith('top')) {
    result.borderLeft = `${width / 2}px solid transparent`;
    result.borderRight = `${width / 2}px solid transparent`;
    result.borderTop = `${height}px solid ${color}`;
    result.borderBottom = '';
    result.bottom = `${-height}px`;
  } else if (placement.startsWith('right')) {
    result.borderTop = `${width / 2}px solid transparent`;
    result.borderBottom = `${width / 2}px solid transparent`;
    result.borderRight = `${height}px solid ${color}`;
    result.borderLeft = '';
    result.left = `${-height}px`;
  } else if (placement.startsWith('left')) {
    result.borderTop = `${width / 2}px solid transparent`;
    result.borderBottom = `${width / 2}px solid transparent`;
    result.borderLeft = `${height}px solid ${color}`;
    result.borderRight = '';
    result.right = `${-height}px`;
  }
  return result;
}
